import { isSupabaseConfigured, supabase } from "../lib/supabase";

const STORAGE_KEY = "almoune_gift_cards";
const TX_STORAGE_KEY = "almoune_gift_card_transactions";

const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

export const giftCardStatusOptions = [
  { value: "active", label: "Active" },
  { value: "used", label: "Utilisée" },
  { value: "expired", label: "Expirée" },
  { value: "blocked", label: "Bloquée" },
];

export const giftCardStatusBadgeClass = (status) => {
  switch (status) {
    case "active": return "bg-success";
    case "used": return "bg-secondary";
    case "expired": return "bg-warning text-dark";
    case "blocked": return "bg-danger";
    default: return "bg-secondary";
  }
};

export const giftCardStatusBadgeLabel = (status) => {
  const opt = giftCardStatusOptions.find((o) => o.value === status);
  return opt ? opt.label : status;
};

export const normalizeGiftCard = (gc) => ({
  id: gc.id || `gc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  code: String(gc.code || "").trim().toUpperCase(),
  initialBalance: Number(gc.initialBalance ?? gc.initial_balance ?? 0),
  currentBalance: Number(gc.currentBalance ?? gc.current_balance ?? 0),
  expiryDate: gc.expiryDate ?? gc.expiry_date ?? null,
  minOrder: Number(gc.minOrder ?? gc.min_order ?? 0),
  status: gc.status || "active",
  recipientName: gc.recipientName ?? gc.recipient_name ?? "",
  note: gc.note || "",
  soldBy: gc.soldBy ?? gc.sold_by ?? null,
  soldToOrderId: gc.soldToOrderId ?? gc.sold_to_order_id ?? null,
  createdAt: gc.createdAt ?? gc.created_at ?? new Date().toISOString(),
  updatedAt: gc.updatedAt ?? gc.updated_at ?? new Date().toISOString(),
});

const normalizeTransaction = (tx) => ({
  id: tx.id || `tx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  giftCardId: tx.giftCardId ?? tx.gift_card_id ?? null,
  orderId: tx.orderId ?? tx.order_id ?? null,
  amountDeducted: Number(tx.amountDeducted ?? tx.amount_deducted ?? 0),
  balanceBefore: Number(tx.balanceBefore ?? tx.balance_before ?? 0),
  balanceAfter: Number(tx.balanceAfter ?? tx.balance_after ?? 0),
  createdAt: tx.createdAt ?? tx.created_at ?? new Date().toISOString(),
});

export const generateGiftCardCode = () => {
  const segment = (len) => {
    let s = "";
    for (let i = 0; i < len; i++) {
      s += CHARS[Math.floor(Math.random() * CHARS.length)];
    }
    return s;
  };
  return `GC-${segment(4)}-${segment(4)}`;
};

/* ── localStorage helpers ── */

const readCardCache = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeGiftCard);
  } catch {
    return [];
  }
};

const writeCardCache = (cards) => {
  const normalized = cards.map(normalizeGiftCard);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

const readTxCache = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(TX_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(normalizeTransaction);
  } catch {
    return [];
  }
};

const writeTxCache = (txs) => {
  const normalized = txs.map(normalizeTransaction);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(TX_STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

/* ── CRUD ── */

export const loadGiftCards = async () => {
  const cached = readCardCache();

  if (!isSupabaseConfigured || !supabase) {
    return cached;
  }

  try {
    const { data, error } = await supabase
      .from("gift_cards")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) return cached;
    return writeCardCache(data.map(normalizeGiftCard));
  } catch {
    return cached;
  }
};

export const saveGiftCard = async (gc) => {
  const normalized = normalizeGiftCard(gc);
  const all = readCardCache();
  const existingIndex = all.findIndex((c) => c.id === normalized.id);
  const updated = existingIndex >= 0
    ? all.map((c, i) => (i === existingIndex ? normalized : c))
    : [...all, normalized];
  const saved = writeCardCache(updated);

  if (!isSupabaseConfigured || !supabase) {
    return saved;
  }

  try {
    await supabase.from("gift_cards").upsert({
      id: normalized.id,
      code: normalized.code,
      initial_balance: normalized.initialBalance,
      current_balance: normalized.currentBalance,
      expiry_date: normalized.expiryDate,
      min_order: normalized.minOrder,
      status: normalized.status,
      recipient_name: normalized.recipientName,
      note: normalized.note,
      sold_by: normalized.soldBy,
      sold_to_order_id: normalized.soldToOrderId,
      created_at: normalized.createdAt,
      updated_at: normalized.updatedAt,
    }, { onConflict: "id" });
  } catch {
    // cache already written
  }

  return saved;
};

export const deleteGiftCard = async (id) => {
  const cards = readCardCache().filter((c) => c.id !== id);
  writeCardCache(cards);

  const txs = readTxCache().filter((t) => t.giftCardId !== id);
  writeTxCache(txs);

  if (!isSupabaseConfigured || !supabase) {
    return true;
  }

  try {
    await supabase.from("gift_cards").delete().eq("id", id);
    await supabase.from("gift_card_transactions").delete().eq("gift_card_id", id);
  } catch (err) {
    throw err;
  }

  return true;
};

/* ── Validation ── */

export const validateGiftCard = (code, subtotal) => {
  if (!code || typeof code !== "string") {
    return { valid: false, giftCard: null, error: "Veuillez entrer un code carte cadeau.", maxDeductible: 0 };
  }

  const normalized = code.trim().toUpperCase();
  const all = readCardCache();
  const gc = all.find((c) => c.code === normalized);

  if (!gc) {
    return { valid: false, giftCard: null, error: "Cette carte cadeau n'existe pas.", maxDeductible: 0 };
  }

  if (gc.status === "blocked") {
    return { valid: false, giftCard: gc, error: "Cette carte cadeau est bloquée.", maxDeductible: 0 };
  }

  if (gc.expiryDate && new Date(gc.expiryDate) < new Date()) {
    gc.status = "expired";
    saveGiftCard(gc);
    return { valid: false, giftCard: gc, error: "Cette carte cadeau a expiré.", maxDeductible: 0 };
  }

  if (gc.currentBalance <= 0) {
    return { valid: false, giftCard: gc, error: "Cette carte cadeau n'a plus de solde.", maxDeductible: 0 };
  }

  if (gc.minOrder > 0 && subtotal < gc.minOrder) {
    return {
      valid: false,
      giftCard: gc,
      error: `Commande minimum de ${gc.minOrder.toFixed(2)} EUR requise pour cette carte cadeau. (Sous-total actuel: ${subtotal.toFixed(2)} EUR)`,
      maxDeductible: 0,
    };
  }

  const maxDeductible = Math.min(gc.currentBalance, subtotal);
  return { valid: true, giftCard: gc, error: null, maxDeductible };
};

/* ── Redemption ── */

export const redeemGiftCard = async (giftCardId, orderId, amount) => {
  const all = readCardCache();
  const gc = all.find((c) => c.id === giftCardId);
  if (!gc) throw new Error("Carte cadeau introuvable");

  const balanceBefore = gc.currentBalance;
  const deduct = Math.min(amount, balanceBefore);
  gc.currentBalance = Number((balanceBefore - deduct).toFixed(2));
  if (gc.currentBalance <= 0) {
    gc.currentBalance = 0;
    gc.status = "used";
  }
  gc.updatedAt = new Date().toISOString();
  await saveGiftCard(gc);

  const tx = normalizeTransaction({
    giftCardId: gc.id,
    orderId,
    amountDeducted: deduct,
    balanceBefore,
    balanceAfter: gc.currentBalance,
  });
  const txs = readTxCache();
  writeTxCache([...txs, tx]);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from("gift_card_transactions").insert({
        id: tx.id,
        gift_card_id: tx.giftCardId,
        order_id: tx.orderId,
        amount_deducted: tx.amountDeducted,
        balance_before: tx.balanceBefore,
        balance_after: tx.balanceAfter,
        created_at: tx.createdAt,
      });
    } catch {
      // cache already written
    }
  }

  return { giftCard: gc, transaction: tx, deducted: deduct };
};

export const refundGiftCard = async (giftCardId, orderId, amount) => {
  const all = readCardCache();
  const gc = all.find((c) => c.id === giftCardId);
  if (!gc) throw new Error("Carte cadeau introuvable");

  const balanceBefore = gc.currentBalance;
  gc.currentBalance = Number((balanceBefore + amount).toFixed(2));
  gc.status = "active";
  gc.updatedAt = new Date().toISOString();
  await saveGiftCard(gc);

  const tx = normalizeTransaction({
    giftCardId: gc.id,
    orderId,
    amountDeducted: -amount,
    balanceBefore,
    balanceAfter: gc.currentBalance,
  });
  const txs = readTxCache();
  writeTxCache([...txs, tx]);

  if (isSupabaseConfigured && supabase) {
    try {
      await supabase.from("gift_card_transactions").insert({
        id: tx.id,
        gift_card_id: tx.giftCardId,
        order_id: tx.orderId,
        amount_deducted: tx.amountDeducted,
        balance_before: tx.balanceBefore,
        balance_after: tx.balanceAfter,
        created_at: tx.createdAt,
      });
    } catch {
      // cache already written
    }
  }

  return { giftCard: gc, transaction: tx };
};

/* ── Batch creation ── */

export const createGiftCardBatch = async ({ count, balance, expiryDate, minOrder, note, recipientName }) => {
  const cards = [];
  for (let i = 0; i < count; i++) {
    const gc = normalizeGiftCard({
      code: generateGiftCardCode(),
      initialBalance: balance,
      currentBalance: balance,
      expiryDate: expiryDate || null,
      minOrder: minOrder || 0,
      status: "active",
      recipientName: recipientName || "",
      note: note || "",
    });
    await saveGiftCard(gc);
    cards.push(gc);
  }
  return cards;
};

/* ── Queries ── */

export const getGiftCardTransactions = (giftCardId) => {
  return readTxCache().filter((tx) => tx.giftCardId === giftCardId);
};

export const loadGiftCardTransactions = async () => {
  const cached = readTxCache();

  if (!isSupabaseConfigured || !supabase) {
    return cached;
  }

  try {
    const { data, error } = await supabase
      .from("gift_card_transactions")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) return cached;
    return writeTxCache(data.map(normalizeTransaction));
  } catch {
    return cached;
  }
};
